import { RouterRootState } from "connected-react-router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Description from "../../../components/description/Description";
import { Topic } from "../../../components/item-content-two/Topic";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { PresetCard } from "../../../components/preset/PresetCard";
import PresetActions from "../../../components/preset/presetActions";
import PresetsDeviceSchedules from "../../../components/preset/presetDeviceSchedules";
import PresetInputs from "../../../components/preset/presetInputs";
import PresetModules from "../../../components/preset/presetModules";
import PresetOutputs from "../../../components/preset/presetOutputs";
import PresetParameters from "../../../components/preset/presetParameters";
import { SystemProfileTypeIds } from "../../../components/preset/presetSettingsWizard";
import { SelectType } from "../../../components/settingFields/SettingSelectField";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { DevicePreset as IODevicePresetModel } from "../../../models/DevicePreset";
import { SimpleItemProfile } from "../../../models/SimpleItemProfile";
import { TabSummary } from "../../../models/TabSummary";
import { DeleteEntityResult } from "../../../models/services/DeleteEntityResult";
import {
    getDeviceProfileNames,
    getIODevicePresetsId,
    storeIODevicePreset,
} from "../../../services/hardwarePresetService";
import { deletePresetSetting } from "../../../services/presetService";
import {
    getErrorMessage,
    showError,
    showLoadItemError,
} from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { useDialog } from "../../../components/dialog/DialogContext";
import AssignWizard from "../../../components/assignWizard/AssignWizard";
import { Lookup } from "../../../models/Lookup";
import {
    assignModulesToPreset,
    getIOModules,
} from "../../../services/ioModuleService";
import { appendFilter } from "../../../utilities/filters";
import { ListQuery } from "../../../models/ListQuery";
import columns from "../../../models/ColumnDefinitions/PresetReaderColumns";
import searchColumns from "../../../models/search/PresetReaderSearch";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { PRESET_TYPES } from "../../../constants/preset-constants";

const getIdFromPath = (path: string) => {
    const splitPath = path.split("/");
    return splitPath[splitPath.length - 1];
};

const initialItem: IODevicePresetModel = {
    id: 0,
    name: "",
    type: "",
    site: "",
    devices: 0,
    siteId: 0,
    profileTypeId: "",
    pInheritsFrom: "",
    inheritsFromId: 0,
    description: "",
    totalDevices: 0,
    totalActions: 0,
    totalParameters: 0,
    totalInputs: 0,
    totalDoorModePatterns: 0,
    totalOutputs: 0,
};

const allItemsPath = "/access-control/hardware/presets";
const itemPath = "/access-control/hardware/device-preset/";

const IODevicePreset: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        {
            label: translate.byKey(TranslationKeys.inputs),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.outputs),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.actions),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.parameters),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.schedules),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        {
            label: translate.byKey(TranslationKeys.modules),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
        // { label: translate.byKey(TranslationKeys.advanced), badge: 0, showInitial: true, isActive: false },
        {
            label: translate.byKey(TranslationKeys.notes),
            badge: 0,
            showInitial: true,
            isActive: true,
        },
    ];
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) =>
        getIdFromPath(state.router.location.pathname)
    );
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>(
        initialItem
    );
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const reportTitlePresetInputModules = translate.byKey(
        TranslationKeys.presetInputModules
    );
    const reportTitlePresetActionsModules = translate.byKey(
        TranslationKeys.presetActionModules
    );

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const [showManagedModal, setShowManagedModal] = useState(false);

    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const dialog = useDialog();

    const onPresetSave = (index) => {
        setTabs(updateTabBadgeTotal(index, tabs[index].badge + 1, tabs));
    };

    const onPresetDeleteUpdateBadge = (index) => {
        setTabs(updateTabBadgeTotal(index, tabs[index].badge - 1, tabs));
    };

    const onDelete = (id: string, index): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deletePresetSetting(id).then(
                (deleteResult) => {
                    resolve(deleteResult);
                    setTabs(
                        updateTabBadgeTotal(index, tabs[index].badge - 1, tabs)
                    );
                    setBusy(false);
                },
                (deleteResult) => {
                    reject(deleteResult);
                    setBusy(false);
                }
            );
        });
    };

    const tabContentList: ContentWrapper[] = [
        {
            content: (
                <PresetInputs
                    onDelete={(id) => onDelete(id, 0)}
                    onSave={(createNew) => {
                        if (createNew) onPresetSave(0);
                    }}
                    profileId={SystemProfileTypeIds.Reader}
                    itemId={itemId}
                    currentItemDetails={currentItem}
                />
            ),
        },
        {
            content: <PresetOutputs itemId={itemId} />,
        },
        {
            content: (
                <PresetActions
                    onDelete={(id) => onDelete(id, 2)}
                    onSave={(createNew) => {
                        if (createNew) onPresetSave(2);
                    }}
                    profileId={SystemProfileTypeIds.Reader}
                    itemId={itemId}
                    currentItemDetails={currentItem}
                    reportTitle={reportTitlePresetActionsModules}
                />
            ),
        },
        {
            content: (
                <PresetParameters
                    onSave={(createNew) => {
                        if (createNew) onPresetSave(3);
                    }}
                    profileId={SystemProfileTypeIds.Reader}
                    itemId={itemId}
                />
            ),
        },
        {
            content: (
                <PresetsDeviceSchedules
                    itemId={itemId}
                    onDelete={(id) => onDelete(id, 4)}
                    onSave={(createNew) => {
                        if (createNew) onPresetSave(4);
                    }}
                    profileType={SystemProfileTypeIds.Reader}
                    onDeleteUpdateBadge={(createNew) => {
                        if (createNew) onPresetDeleteUpdateBadge(4);
                    }}
                />
            ),
        },
        {
            content: (
                <PresetModules itemId={itemId}>
                    {/* <button
                        type="button"
                        className="flex ml-8 px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                        onClick={() => {
                            setShowManagedModal(true);
                        }}
                    >
                        {translate.byKey(TranslationKeys.manage)}
                    </button> */} {/* Needs backend support for correctly clearing old actions*/}
                </PresetModules>
            ),
        },
        {
            content: (
                <Description title={translate.byKey(TranslationKeys.notes)} />
            ),
        },
    ];

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        return newTabs;
    };

    const topic: Topic = {
        accessor: "trnName",
        title: translate.byKey(TranslationKeys.moduleAndIOPresets),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    };

    const reloadPage = async () => {
        try {
            setTabs([]);
            let ioDevicePreset;

            if (itemId) {
                ioDevicePreset = await getIODevicePresetsId(itemId);
                setSelectedItem({label: ioDevicePreset.name, value: ioDevicePreset.id.toString(), isDefault: ioDevicePreset.isDefault})
                setCurrentItem(ioDevicePreset);
                // update the tab badge counts
                const updatedTabs = updateTabBadgeTotal(
                    0,
                    ioDevicePreset?.totalInputs,
                    updateTabBadgeTotal(
                        1,
                        0,
                        updateTabBadgeTotal(
                            2,
                            ioDevicePreset?.totalActions,
                            updateTabBadgeTotal(
                                3,
                                ioDevicePreset?.totalParameters,
                                updateTabBadgeTotal(
                                    4,
                                    ioDevicePreset?.totalDoorModePatterns,
                                    updateTabBadgeTotal(
                                        5,
                                        ioDevicePreset?.totalDevices,
                                        initialTabs
                                    )
                                )
                            )
                        )
                    )
                );
                setTabs(updatedTabs);
            } else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[6]]);
            }

            setBreadcrumb([
                {
                    value: "/access-control/hardware",
                    displayName: translate.byKey(TranslationKeys.hardware),
                },
                {
                    value: allItemsPath,
                    displayName: translate.byKey(TranslationKeys.preset),
                },
                {
                    value: itemPath + `${itemId}`,
                    displayName: `${ioDevicePreset?.name ?? ""}`,
                },
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => {
                history.push(allItemsPath);
            });
        }
    };

    useEffect(() => {
        setTabs([]);
        setItemId(pageId);
    }, [pageId]);

    useEffect(() => {
        reloadPage();
    }, [itemId]);

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId
            ? tabContentList[index ?? 0]
            : tabContentList[6];
        return selectedTabContent.content;
    };

    const saveIODevicePreset = (values) => {
        const newIODevicePreset: IODevicePresetModel = {
            ...currentItem,
            name: values.name,
            id: values.id,
            pInheritsFrom: values.pInheritsFrom,
            inheritsFromId: values.inheritsFromId,
            siteId: currentItem?.siteId ? currentItem?.siteId : siteId,
            description: values.description,
        };

        const isNew = newIODevicePreset.id ? false : true;

        setBusy(true);
        storeIODevicePreset(newIODevicePreset, isNew)
            .then(async (value: any) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                setCurrentItem(newIODevicePreset);
                toast.success(
                    translate.byKey(TranslationKeys.ioDevicePresetSaved)
                );
            })
            .catch((failure) => {
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.error),
                    message: getErrorMessage(failure),
                });
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const dataMethod = (listParameters?: ListQuery) =>
        getIOModules({
            ...listParameters,
            filters: appendFilter(
                itemId,
                "Profile",
                "profile.id",
                listParameters?.filters ?? []
            ),
        });

    const getAllIOModules = async () => {
        try {
            const ioModules = (await dataMethod()).rows;
            const ioModuleIds = ioModules.map((ioModule) =>
                ioModule.id.toString()
            );
            setSelectedIds(ioModuleIds);
            setOldSelectedIds(ioModuleIds);
        } catch (error) {
            showError(error, toast.warn);
        }
    };

    useEffect(() => {
        if (showManagedModal) {
            getAllIOModules();
        }
    }, [showManagedModal]);

    const assignPresets = async () => {
        const newSelectedIds = selectedIds.filter((value) => value !== "");
        setOldSelectedIds(newSelectedIds);


        const toBeAdded = newSelectedIds.filter((value) => {
            if (!!!oldSelectedIds.includes(value)) {
                return value;
            }
        });

        setBusy(true);
        assignModulesToPreset(itemId, toBeAdded)
        .then(()=>{
            let newTab = [...tabs];
            newTab[5].badge = newSelectedIds.length;
            setTabs(newTab);
        }).catch (error=> {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        }).finally(()=>{
            setBusy(false);
        });
    };

    return (
        <div className="flex h-full">
            <TopicContextProvider
                topic={topic}
                dataMethod={(listParameters) => getDeviceProfileNames(PRESET_TYPES.IOMODULE, listParameters)}
                itemId={itemId}
                selectedItem={selectedItem}
            >
                <ItemContentTabTwo>
                    <SimpleItemDetails
                        breadcrumb={breadcrumb}
                        busy={busy}
                        getTabContent={getTabContent}
                        itemProfile={currentItem}
                        onBack={() => history.push(allItemsPath)}
                        saveDetails={saveIODevicePreset}
                        tabs={tabs}
                        itemPath={itemPath}
                        initialValues={currentItem}
                    >
                        <PresetCard
                            presetType={SelectType.ioDeviceProfile}
                        ></PresetCard>
                    </SimpleItemDetails>
                </ItemContentTabTwo>
            </TopicContextProvider>

            <AssignWizard
                isOpen={showManagedModal}
                onClose={() => setShowManagedModal(false)}
                title={translate.byKey(TranslationKeys.assignModules)}
                lookup={lookup}
                dataMethod={getIOModules}
                columnDescriptors={{ columns, searchColumns }}
                handleOnSubmit={assignPresets}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                alwaysSelectedAndDisabled={oldSelectedIds}
            />
        </div>
    );
};

export default IODevicePreset;
